import React from "react";
import { CheckCircle2, Copy } from "lucide-react";
import { toast } from "react-toastify";
import { GroupReservationRequest } from "@/api/types/group-reservation";

const STATUS_LABELS: Record<string, string> = {
  submitted: "접수 완료",
  reviewing: "검토 중",
  reservation_created: "예약 연결",
  awaiting_bank_transfer: "입금 대기",
  bank_transfer_confirmed: "입금 확인",
  approved: "승인 완료",
  reverted: "되돌림",
  rejected: "반려",
  canceled: "취소",
};

const WEEKDAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];
const formatKoreanDate = (value?: string | null) => {
  if (!value) return value ?? "";
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return String(value);
  const weekday = WEEKDAY_LABELS[new Date(year, month - 1, day).getDay()];
  return `${year}년 ${month}월 ${day}일 (${weekday})`;
};

const formatAmount = (value?: number | null) => `${Number(value || 0).toLocaleString()}원`;

type RequestCompletePanelProps = {
  request: GroupReservationRequest;
};

export default function RequestCompletePanel({ request }: RequestCompletePanelProps) {
  const sourceLabel = request.source_type === "agency" ? request.agency_label || "대리점" : "일반";
  const dateLabel =
    request.visit_start_date === request.visit_end_date
      ? formatKoreanDate(request.visit_start_date)
      : `${formatKoreanDate(request.visit_start_date)} ~ ${formatKoreanDate(request.visit_end_date)}`;

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(request.request_code);
      toast.success("접수번호를 복사했습니다.");
    } catch (error) {
      console.error(error);
      toast.error("접수번호 복사에 실패했습니다.");
    }
  };

  return (
    <div className="px-5 py-8">
      <style>{`
        @keyframes grCheckPop {
          0% { transform: scale(0.3); opacity: 0; }
          60% { transform: scale(1.12); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes grCheckRing {
          0% { transform: scale(0.8); opacity: 0.5; }
          70% { transform: scale(1.6); opacity: 0; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .gr-check-pop, .gr-check-ring { animation: none !important; }
          .gr-check-ring { display: none; }
        }
      `}</style>
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-6 text-center">
        <div className="relative mx-auto mb-4 h-12 w-12">
          <span
            className="gr-check-ring absolute inset-0 rounded-full bg-emerald-400"
            style={{ animation: "grCheckRing 1.4s ease-out 0.15s 2" }}
          />
          <div
            className="gr-check-pop relative flex h-12 w-12 items-center justify-center rounded-full bg-white text-emerald-700 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.06)]"
            style={{ animation: "grCheckPop 0.5s cubic-bezier(0.34,1.56,0.64,1) both" }}
          >
            <CheckCircle2 size={28} />
          </div>
        </div>
        <h1 className="text-xl font-black text-gray-900 text-balance">단체 예약 신청이 접수되었습니다</h1>
        <p className="mt-2 text-sm leading-6 text-gray-700 text-pretty">
          담당자가 신청 내용을 확인한 뒤 예약 일정과 입금 안내를 연락드립니다.
        </p>
      </div>

      {/* 접수번호 강조 + 복사 */}
      <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl bg-white px-4 py-4 shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-700">접수번호</p>
          <p className="mt-1 truncate text-sm font-semibold text-gray-900 tabular-nums">{request.request_code}</p>
        </div>
        <button
          type="button"
          onClick={handleCopyCode}
          className="flex shrink-0 items-center gap-1.5   px-3 py-2 text-sm font-bold text-gray-700 transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 active:scale-[0.97]"
        >
          <Copy size={15} />
        
        </button>
      </div>

      <dl className="mt-4 divide-y divide-gray-100 rounded-2xl bg-white shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
        <div className="flex items-center justify-between gap-4 px-4 py-3">
          <dt className="text-sm font-semibold text-gray-900">접수경로</dt>
          <dd className="text-sm font-semibold text-gray-700">{sourceLabel}</dd>
        </div>
        <div className="flex items-center justify-between gap-4 px-4 py-3">
          <dt className="text-sm font-semibold text-gray-900">단체명</dt>
          <dd className="text-sm font-bold text-gray-700">{request.group_name}</dd>
        </div>
        <div className="flex items-center justify-between gap-4 px-4 py-3">
          <dt className="text-sm font-semibold text-gray-900">희망일</dt>
          <dd className="text-sm font-semibold text-gray-700 tabular-nums">{dateLabel}</dd>
        </div>
        <div className="flex items-center justify-between gap-4 px-4 py-3">
          <dt className="text-sm font-semibold text-gray-900">입금/확정 상태</dt>
          <dd className="text-sm font-semibold text-gray-700">{STATUS_LABELS[request.status] || request.status}</dd>
        </div>
        {request.selected_options?.length ? (
          <div className="px-4 py-3">
            <dt className="text-sm font-semibold text-gray-900">선택 옵션</dt>
            <dd className="mt-2 space-y-1">
              {request.selected_options.map((option) => (
                <div key={option.id} className="flex items-center justify-between gap-4 text-sm">
                  <span className="font-semibold text-gray-700 tabular-nums">
                    {option.name_snapshot} x {option.quantity?.toLocaleString()}개
                  </span>
                  <span className="font-semibold text-gray-700 tabular-nums">{formatAmount(option.total_amount)}</span>
                </div>
              ))}
            </dd>
          </div>
        ) : null}
        {request.estimated_total_amount ? (
          <div className="flex items-center justify-between gap-4 px-4 py-3">
            <dt className="text-sm font-semibold text-gray-900">예상 옵션 합계</dt>
            <dd className="text-sm font-semibold text-gray-700 tabular-nums">{formatAmount(request.estimated_total_amount)}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}
